export interface EvaluationComparison {
  nominationId: number;
  nominationNumber: string;
  name: string;
  humanScrubStatusId?: number;
  humanScrubStatusDisplayName?: string;
  humanScrubSourceCode?: string;
  aiDisqualificationStatus?: string;
  aiDisqualificationResult?: string;
  humanAggregateScore?: number;
  aiOverallScore?: number;
  aiOverallRationale?: string;
  aiDisqualificationReason?: string;
  aiDisqualificationRationale?: string;
  winnerRole?: string;
  humanOutcome?: string;
}

export type HumanScrubBand = 'human-disqualified' | 'human-passed' | 'no-human-label';

export type AiDisqualificationBucket =
  | 'ai-rule-disqualified'
  | 'ai-llm-disqualified'
  | 'ai-passed'
  | 'ai-did-not-complete';

export const humanScrubBands: HumanScrubBand[] = ['human-disqualified', 'human-passed', 'no-human-label'];

export const aiDisqualificationBuckets: AiDisqualificationBucket[] = [
  'ai-rule-disqualified',
  'ai-llm-disqualified',
  'ai-passed',
  'ai-did-not-complete',
];

export function humanScrubBand(row: EvaluationComparison): HumanScrubBand {
  return humanScrubBands.includes(row.humanOutcome as HumanScrubBand)
    ? (row.humanOutcome as HumanScrubBand)
    : 'no-human-label';
}

export function aiDisqualificationBucket(row: EvaluationComparison): AiDisqualificationBucket {
  if (row.humanScrubSourceCode === 'Rule') return 'ai-rule-disqualified';
  if (row.aiDisqualificationStatus === 'Completed') {
    if (row.aiDisqualificationResult === 'Disqualify') return 'ai-llm-disqualified';
    if (row.aiDisqualificationResult === 'Pass') return 'ai-passed';
  }
  return 'ai-did-not-complete';
}

export interface ScorePair {
  nominationNumber: string;
  humanAggregateScore: number;
  aiOverallScore: number;
}

export function scorePairs(rows: EvaluationComparison[]): ScorePair[] {
  return rows.flatMap((row) =>
    row.humanAggregateScore != null && row.aiOverallScore != null
      ? [{
          nominationNumber: row.nominationNumber,
          humanAggregateScore: row.humanAggregateScore,
          aiOverallScore: row.aiOverallScore,
        }]
      : [],
  );
}

export const WINNER_ROLE = 'Winner';

export interface WinnerScoredLowerRow {
  nominationNumber: string;
  name: string;
  humanAggregateScore: number;
  aiOverallScore: number;
  aiOverallRationale?: string;
}

export function winnersScoredLower(rows: EvaluationComparison[]): WinnerScoredLowerRow[] {
  const pairs = scorePairs(rows);
  if (pairs.length < 2) return [];
  const hasVariance = (values: number[]): boolean => new Set(values).size > 1;
  if (!hasVariance(pairs.map((p) => p.humanAggregateScore)) || !hasVariance(pairs.map((p) => p.aiOverallScore))) return [];
  const humanRanks = averageRanks(pairs.map((p) => p.humanAggregateScore));
  const aiRanks = averageRanks(pairs.map((p) => p.aiOverallScore));
  const pct = (rank: number): number => (rank - 1) / (pairs.length - 1);
  const rowByNumber = new Map(rows.map((r) => [r.nominationNumber, r]));
  return pairs
    .map((pair, index) => ({ pair, humanPct: pct(humanRanks[index]), aiPct: pct(aiRanks[index]) }))
    .filter(({ pair, humanPct, aiPct }) =>
      aiPct < humanPct && rowByNumber.get(pair.nominationNumber)?.winnerRole === WINNER_ROLE)
    .sort((a, b) =>
      (b.humanPct - b.aiPct) - (a.humanPct - a.aiPct)
      || a.pair.nominationNumber.localeCompare(b.pair.nominationNumber))
    .map(({ pair }) => {
      const row = rowByNumber.get(pair.nominationNumber)!;
      return {
        nominationNumber: pair.nominationNumber,
        name: row.name,
        humanAggregateScore: pair.humanAggregateScore,
        aiOverallScore: pair.aiOverallScore,
        aiOverallRationale: row.aiOverallRationale,
      };
    });
}

export function spearmanRankCorrelation(pairs: ScorePair[]): number | null {
  if (pairs.length < 2) return null;
  const humanRanks = averageRanks(pairs.map((p) => p.humanAggregateScore));
  const aiRanks = averageRanks(pairs.map((p) => p.aiOverallScore));
  return pearson(humanRanks, aiRanks);
}

type ScoreAgreementStrength = 'weak' | 'moderate' | 'strong' | 'very strong';

export type ScoreAgreementBand = 'negligible' | `${ScoreAgreementStrength} ${'positive' | 'negative'}`;

export function scoreAgreementBand(rho: number): ScoreAgreementBand {
  const magnitude = Math.abs(rho);
  if (magnitude < 0.2) return 'negligible';
  const strength: ScoreAgreementStrength =
    magnitude < 0.4 ? 'weak' : magnitude < 0.6 ? 'moderate' : magnitude < 0.8 ? 'strong' : 'very strong';
  return `${strength} ${rho >= 0 ? 'positive' : 'negative'}`;
}

export function averageRanks(values: number[]): number[] {
  const indexed = values.map((value, index) => ({ value, index })).sort((a, b) => a.value - b.value);
  const ranks = new Array<number>(values.length);
  let start = 0;
  while (start < indexed.length) {
    let end = start;
    while (end + 1 < indexed.length && indexed[end + 1].value === indexed[start].value) end += 1;
    const averageRank = (start + end) / 2 + 1;
    for (let i = start; i <= end; i += 1) ranks[indexed[i].index] = averageRank;
    start = end + 1;
  }
  return ranks;
}

function pearson(xs: number[], ys: number[]): number | null {
  const meanX = xs.reduce((sum, v) => sum + v, 0) / xs.length;
  const meanY = ys.reduce((sum, v) => sum + v, 0) / ys.length;
  let covariance = 0;
  let varianceX = 0;
  let varianceY = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    covariance += dx * dy;
    varianceX += dx * dx;
    varianceY += dy * dy;
  }
  if (varianceX === 0 || varianceY === 0) return null;
  return covariance / Math.sqrt(varianceX * varianceY);
}
